import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import Radom, {
  AddressSuggestionDetails,
  CheckoutSession,
  ICreateCustomer,
  IDiscountCode,
  IManagedPaymentMethodDetails,
  IManagedPaymentStatus,
  IManagedTokenDelegatePaymentMethodDetails,
  OpenBankingPaymentData,
  PaymentMethod,
  ProductWithQuantity
} from '../api/Radom'
import Checkout, { CheckoutData, zeroTravelRuleFieldsCollected } from '../components/Checkout'
import PayWidget, { type CheckoutParams } from '../components/PayWidget'
import Spinner from '../components/Spinner'
import { Chevron } from '../icons/Chevron'
import { getTotalSum } from '../util/Products'
import { RADOM_COLORS } from '../util/Constants'
import { CheckmarkWrapper } from '../components/Receipt'
import { PaymentSuccessMessage, PaymentUnderReviewMessage } from '../components/PaymentOutcome'
import { clearPaymentSession, loadPaymentSession, savePaymentSession } from '../util/PaymentSession'
import Checkmark from '../icons/Checkmark'
import { isValidPhone, resolveNavigationUrl } from '../util/Util'
import RadomLogo from '../icons/Logo2'
import { PrimaryButton } from '../components/Button'
import { formatCurrency } from '../util/Currencies'
import {
  InputLabel,
  TextInput,
  TextInputWithLabel,
  TextInputWithLabelWithSuggestions,
  InlineInputError,
  INPUT_ERROR_STYLE
} from '../components/Input'
import { IvyCheckout } from '@getivy/react-sdk'
import '@getivy/react-sdk/dist/index.css'
import { t, tBank, setLanguage, Language } from '../util/i18n'
import { Country, ICountry } from 'country-state-city'
import DateSelector from '../components/DateSelector'
import KycView from './KycView'
import _ from 'lodash'
import Crossmark from '../icons/Crossmark'
import PendingSpinner from '../icons/PendingSpinner'
import CountrySelector from '../components/CountrySelector'
import { subYears } from 'date-fns'
import { ManagedPaymentMethod } from '../util/Managed'
import { OnrampComponent } from '../components/OpenBanking'
import { LinkArrow } from '../icons/LinkArrow'
import USDCLogo from '../icons/USDCLogo'
import PaymentMethodDisplay from '../components/PaymentMethodDisplay'
import Edit from '../icons/Edit'
import { trackCheckoutFunnelStep } from '../util/CheckoutFunnel'
import PageLoadState from '../components/PageLoadState'

const CURRENCY_USD = 'USD'
const DEFAULT_FIAT_CURRENCY = CURRENCY_USD

const isSecureHostedUrl = (value?: string): boolean => {
  if (!value) {
    return false
  }

  try {
    const parsedUrl = new URL(value)
    return parsedUrl.protocol === 'https:'
  } catch {
    return false
  }
}

const formatOpenBankingErrorMessage = (message?: string): string => {
  const normalized = (message ?? '').trim()
  if (!normalized) {
    return 'We could not start the bank transfer. Please try again.'
  }

  if (normalized.toLowerCase() === 'kyc verified false') {
    return 'We could not verify your details. Please review your information and try again.'
  }

  return normalized
}

const normalizeOpenBankingProviderStatus = (status?: string): string =>
  (status ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_')

const OPEN_BANKING_PROVIDER_CONFIRMED_STATUSES = new Set([
  'paid',
  'success',
  'succeeded',
  'complete',
  'completed'
])

const OPEN_BANKING_PROVIDER_FAILED_STATUSES = new Set([
  'failed',
  'canceled',
  'cancelled'
])

const OPEN_BANKING_PROVIDER_AUTHORIZED_STATUSES = new Set([
  'authorized',
  'waiting_for_payment'
])

// The customer has approved the payment at their bank and the transfer is on
// its way, or the provider is completing its checks on it.
const OPEN_BANKING_PROVIDER_IN_FLIGHT_STATUSES = new Set([
  'authorized',
  'waiting_for_payment',
  'finalizing'
])

const hasOpenBankingDestinationAddress = (address?: string | null): boolean =>
  typeof address === 'string' && address.trim().length > 0

const isOpenBankingProviderConfirmed = (data?: OpenBankingPaymentData): boolean =>
  OPEN_BANKING_PROVIDER_CONFIRMED_STATUSES.has(normalizeOpenBankingProviderStatus(data?.status))

const isOpenBankingProviderFailed = (data?: OpenBankingPaymentData): boolean =>
  OPEN_BANKING_PROVIDER_FAILED_STATUSES.has(normalizeOpenBankingProviderStatus(data?.status))

/**
 * A bank payment the customer has already approved can still complete after
 * the session's expiry time. While one is in flight the session is not
 * expired for the customer: telling them to start again would have them pay twice.
 */
const isOpenBankingPaymentInFlight = (data?: OpenBankingPaymentData): boolean =>
  OPEN_BANKING_PROVIDER_IN_FLIGHT_STATUSES.has(normalizeOpenBankingProviderStatus(data?.status))

interface OpenBankingReturnStatusCopy {
  title: string
  body: string
  isError: boolean
}

const getOpenBankingSchemeName = (data?: OpenBankingPaymentData): string => {
  const scheme = data?.paymentScheme?.trim()
  if (scheme) {
    return scheme
  }

  if (data?.instantPaymentScheme === true) {
    return 'instant bank transfer'
  }

  if (data?.instantPaymentScheme === false) {
    return 'standard bank transfer'
  }

  return 'bank transfer'
}

const isStandardOpenBankingScheme = (data?: OpenBankingPaymentData): boolean => {
  const scheme = data?.paymentScheme?.trim().toLowerCase()
  return data?.instantPaymentScheme === false ||
    scheme === 'sepa credit' ||
    scheme === 'bacs / chaps' ||
    scheme === 'bacs' ||
    scheme === 'chaps'
}

const getOpenBankingReturnStatusCopy = (
  data?: OpenBankingPaymentData,
  sessionStatus?: string
): OpenBankingReturnStatusCopy => {
  if (sessionStatus === 'expired' && !isOpenBankingPaymentInFlight(data)) {
    return {
      title: 'Payment expired',
      body: 'The checkout session expired before the bank transfer completed. Please start a new payment.',
      isError: true
    }
  }

  if (sessionStatus === 'cancelled') {
    return {
      title: 'Payment cancelled',
      body: 'The checkout session was cancelled before the bank transfer completed.',
      isError: true
    }
  }

  const status = normalizeOpenBankingProviderStatus(data?.status)
  const schemeName = getOpenBankingSchemeName(data)

  if (status === 'canceled' || status === 'cancelled') {
    return {
      title: 'Payment cancelled',
      body: 'The bank authorization was cancelled. You can try again or use another bank.',
      isError: true
    }
  }

  if (status === 'failed') {
    return {
      title: 'Payment unsuccessful',
      body: 'The bank transfer was not completed. Please try again or use another bank.',
      isError: true
    }
  }

  if (status === 'finalizing') {
    return {
      title: 'Payment received',
      body: 'We have received the transfer and are completing final checks. This usually takes a few minutes.',
      isError: false
    }
  }

  if (isOpenBankingProviderConfirmed(data)) {
    return {
      title: 'Payment received',
      body: 'The transfer has been received. We are updating the payment status now.',
      isError: false
    }
  }

  if (OPEN_BANKING_PROVIDER_AUTHORIZED_STATUSES.has(status)) {
    if (isStandardOpenBankingScheme(data)) {
      return {
        title: 'Transfer sent',
        body: `Your bank has sent the payment via ${schemeName}. Standard transfers can take longer to arrive, so we will complete the payment once it reaches us.`,
        isError: false
      }
    }

    return {
      title: 'Transfer authorized',
      body: `Your bank has authorized the ${schemeName}. We are waiting for settlement confirmation before completing the payment.`,
      isError: false
    }
  }

  if (status === 'processing') {
    return {
      title: 'Payment started',
      body: 'Follow the bank authorization steps. We will update this page as soon as your bank sends a status.',
      isError: false
    }
  }

  return {
    title: 'Confirming payment',
    body: 'Please keep this page open while we confirm the bank transfer.',
    isError: false
  }
}

const Container = styled.div`
  width: 100vw;
  height: 100%;
  height: 100vh;
  background-color: white;
  font-size: 14px;
  overflow: hidden;

  @media (max-width: 800px) {
    min-height: 100vh;
    height: auto;
    overflow: visible;
  }
`

interface kycData {
  name: string
  phoneNumber: string
  countryOfResidence: string
  // city: string
  address: string
  // postalCode: string
  dateOfBirth: string
  //
  passportFile?: string
  idCardFile?: string
  selfieFile?: string
}

interface ManualOpenBankingAddress {
  addressLine1: string
  addressLine2: string
  townOrCity: string
  postalCode: string
  stateOrProvince: string
}

type OpenBankingValidationField =
  'name' |
  'dateOfBirth' |
  'countryOfResidence' |
  'address' |
  'addressLine1' |
  'townOrCity' |
  'phoneNumber'

type OpenBankingValidationErrors = Partial<Record<OpenBankingValidationField, string>>

enum OpenBankingRenderStates {
  onramp,
  billing,
  summary
}

const hasCompleteOpenBankingCustomerDetails = (data: kycData, email?: string): boolean => {
  return data.name.trim().length > 0 &&
    data.dateOfBirth.trim().length > 0 &&
    data.countryOfResidence.trim().length > 0 &&
    data.address.trim().length > 0 &&
    isValidPhone(data.phoneNumber) &&
    (email?.trim().length ?? 0) > 0
}

const compactAddressParts = (...parts: Array<string | undefined>): string =>
  parts
    .map(part => part?.trim())
    .filter((part): part is string => Boolean(part && part.length > 0))
    .join(', ')

const openBankingAddressCountryParts = new Set([
  'austria',
  'belgium',
  'bulgaria',
  'croatia',
  'cyprus',
  'czech republic',
  'denmark',
  'estonia',
  'finland',
  'france',
  'germany',
  'greece',
  'hungary',
  'iceland',
  'ireland',
  'italy',
  'latvia',
  'liechtenstein',
  'lithuania',
  'luxembourg',
  'malta',
  'netherlands',
  'norway',
  'poland',
  'portugal',
  'romania',
  'slovakia',
  'slovenia',
  'spain',
  'sweden',
  'united kingdom',
  'uk'
])

const removeTrailingCountryPart = (parts: string[]): string[] => {
  const lastPart = parts[parts.length - 1]?.toLowerCase()
  if (parts.length > 2 && lastPart && openBankingAddressCountryParts.has(lastPart)) {
    return parts.slice(0, -1)
  }

  return parts
}

const splitTownAndPostalCode = (value: string): Pick<ManualOpenBankingAddress, 'townOrCity' | 'postalCode'> => {
  const leadingPostalCodeMatch = value.match(/^(\d{4,6})\s+(.+)$/)
  if (leadingPostalCodeMatch) {
    return {
      townOrCity: leadingPostalCodeMatch[2].trim(),
      postalCode: leadingPostalCodeMatch[1].trim()
    }
  }

  const trailingPostalCodeMatch = value.match(/^(.+?)\s+([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})$/i)
  if (trailingPostalCodeMatch) {
    return {
      townOrCity: trailingPostalCodeMatch[1].trim(),
      postalCode: trailingPostalCodeMatch[2].toUpperCase().trim()
    }
  }

  return {
    townOrCity: value,
    postalCode: ''
  }
}

const parseOpenBankingAddress = (address: string): ManualOpenBankingAddress => {
  const parts = removeTrailingCountryPart(
    address
      .split(',')
      .map(part => part.trim())
      .filter(part => part.length > 0)
  )

  if (parts.length === 2) {
    const { townOrCity, postalCode } = splitTownAndPostalCode(parts[1])
    return {
      addressLine1: parts[0],
      addressLine2: '',
      townOrCity,
      stateOrProvince: '',
      postalCode
    }
  }

  return {
    addressLine1: parts[0] ?? '',
    addressLine2: '',
    townOrCity: parts[1] ?? '',
    stateOrProvince: parts.length >= 4 ? parts[2] ?? '' : '',
    postalCode: parts.length >= 4 ? parts[3] ?? '' : parts[2] ?? ''
  }
}

const composeManualOpenBankingAddress = (address: ManualOpenBankingAddress): string =>
  compactAddressParts(
    address.addressLine1,
    address.addressLine2,
    address.townOrCity,
    address.stateOrProvince,
    address.postalCode
  )

const normalizeOpenBankingDob = (dateOfBirth: string): string => {
  if (dateOfBirth.trim().length === 0) {
    return ''
  }

  const parsedDate = new Date(dateOfBirth)
  if (Number.isNaN(parsedDate.getTime())) {
    return dateOfBirth
  }

  return parsedDate.toLocaleDateString('en-CA')
}

const buildOpenBankingCustomerPayload = (
  data: kycData,
  extra: Partial<ICreateCustomer> = {}
): ICreateCustomer => {
  const normalizedDob = normalizeOpenBankingDob(data.dateOfBirth)

  return {
    name: data.name,
    phone: data.phoneNumber,
    address: data.address,
    billingAddress: data.address,
    dateOfBirth: normalizedDob,
    dob: normalizedDob,
    residenceCountry: data.countryOfResidence,
    ...extra
  }
}

enum PaymentStatus {
  INITIAL = 'initial',
  KYC = 'kyc',
  KYC_PENDING_VIEW = 'kyc_pending_view',
  WAITING = 'waiting',
  SUCCESS = 'success',
  Expired = 'expired',
  ERROR = 'error'
}

/**
 * The on-ramp amount to show for a session, or undefined when the session
 * carries none. Before a payment the amount is an estimate, and there is none
 * while no price is available.
 */
const formatOnrampAmount = (amount?: number | string | null): string | undefined => {
  if (amount === undefined || amount === null || amount === '') {
    return undefined
  }
  const value = Number(amount)
  return Number.isFinite(value) ? value.toFixed(2) : undefined
}

// Stands in for an amount that cannot be shown.
const NO_AMOUNT = '–'

interface RenderOpenBankingInitialProps {
  renderState: OpenBankingRenderStates
  setRenderState: React.Dispatch<React.SetStateAction<OpenBankingRenderStates>>
  checkoutData: CheckoutData | undefined
  totalSum: number
  currency: string
  isPreProcessingPayment: boolean
  onrampAmount?: number | string | null
  setOpenBankingTotalUsdRate: React.Dispatch<React.SetStateAction<number>>
  openBankingCheckoutUrl: string
  setOpenBankingCheckoutUrl: React.Dispatch<React.SetStateAction<string>>
  randomAddress: string
  userKycData: kycData
  setUserKycData: React.Dispatch<React.SetStateAction<kycData>>
  addressSuggestionList: string[]
  setAddressSuggestionList: React.Dispatch<React.SetStateAction<string[]>>
  allCountries: ICountry[]
  debouncedFetch: (value: string, isoCode: string, setter: (res: string[]) => void) => void
  checkMarkOpenbanking: boolean
  setCheckMarkOpenBanking: (val: boolean) => void
  errorMessage: string
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>
  setOpenbankPayStatus: React.Dispatch<React.SetStateAction<PaymentStatus>>
}

const RenderOpenBankingInitial = ({
  renderState,
  setRenderState,
  checkoutData,
  totalSum,
  currency,
  isPreProcessingPayment,
  onrampAmount,
  randomAddress,
  userKycData,
  setUserKycData,
  addressSuggestionList,
  setAddressSuggestionList,
  allCountries,
  debouncedFetch,
  checkMarkOpenbanking,
  setCheckMarkOpenBanking,
  errorMessage,
  setErrorMessage,
  setOpenbankPayStatus,
  setOpenBankingCheckoutUrl
}: RenderOpenBankingInitialProps): ReactElement => {
  const { checkoutId } = useParams()
  // Without a price there is no estimate of what the payment buys, and the
  // customer is not asked to continue from a screen that cannot show it.
  const onrampAmountText = formatOnrampAmount(onrampAmount)
  const hasOnrampAmount = onrampAmountText !== undefined
  const trackedOpenBankingStepsRef = useRef<Set<string>>(new Set())
  const trackOpenBankingStep = useCallback((step: 'travelRuleCompleted' | 'paymentInitiated') => {
    if (!checkoutId || trackedOpenBankingStepsRef.current.has(step)) {
      return
    }

    trackedOpenBankingStepsRef.current.add(step)
    void trackCheckoutFunnelStep({
      sourceType: 'checkoutSession',
      sourceId: checkoutId,
      rail: 'openBanking',
      step
    })
  }, [checkoutId])
  const fromMethod: ManagedPaymentMethod = useMemo(() => {
    if (checkoutData?.currency === 'EUR') {
      return {
        name: 'EUR',
        ticker: 'EUR',
        network: 'Fiat',
        token: 'EUR',
        currencyName: 'Euro',
        isWalletSupported: false
      }
    } else {
      return {
        name: 'GBP',
        ticker: 'GBP',
        network: 'Fiat',
        token: 'GBP',
        currencyName: 'British Pound',
        isWalletSupported: false
      }
    }
  }, [checkoutData?.gateway.openBanking])
  const toMethod: ManagedPaymentMethod = useMemo(() => {
    return {
      name: 'USDC',
      ticker: 'USDC',
      network: 'Ethereum',
      currencyName: 'USDC',
      isWalletSupported: false
    }
  }, [checkoutData?.gateway.openBanking])

  const onBack = (): void => {
    if (renderState === OpenBankingRenderStates.billing) {
      setRenderState(OpenBankingRenderStates.onramp)
    } else if (renderState === OpenBankingRenderStates.summary) {
      setRenderState(OpenBankingRenderStates.billing)
    }
  }

  const onChangeFullName = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUserKycData(prev => ({ ...prev, name: e.target.value }))
  }, [])

  const [isManualAddressEntry, setIsManualAddressEntry] = useState(false)
  const [manualAddress, setManualAddress] = useState<ManualOpenBankingAddress>(() =>
    parseOpenBankingAddress(userKycData.address)
  )
  const [validationErrors, setValidationErrors] = useState<OpenBankingValidationErrors>({})

  const fieldErrorStyle = (field: OpenBankingValidationField): React.CSSProperties => ({
    ...(validationErrors[field] ? INPUT_ERROR_STYLE : undefined)
  })

  const clearValidationErrors = (...fields: OpenBankingValidationField[]): void => {
    setValidationErrors(currentErrors => {
      if (fields.every(field => !currentErrors[field])) {
        return currentErrors
      }

      return Object.fromEntries(
        Object.entries(currentErrors).filter(([field]) => !fields.includes(field as OpenBankingValidationField))
      ) as OpenBankingValidationErrors
    })
  }

  const focusValidationField = (field: OpenBankingValidationField): void => {
    window.requestAnimationFrame(() => {
      const fieldElement = document.querySelector<HTMLElement>(`[data-open-banking-field="${field}"]`)
      fieldElement?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      const focusable = fieldElement?.matches('input')
        ? fieldElement
        : fieldElement?.querySelector<HTMLElement>('input, button, [tabindex]:not([tabindex="-1"])')
      focusable?.focus()
    })
  }

  useEffect(() => {
    if (!isManualAddressEntry) {
      setManualAddress(parseOpenBankingAddress(userKycData.address))
    }
  }, [isManualAddressEntry, userKycData.address])

  const enterManualAddressEntry = (): void => {
    const parsedAddress = parseOpenBankingAddress(userKycData.address)
    setAddressSuggestionList([])
    setManualAddress(parsedAddress)
    setUserKycData(prev => ({
      ...prev,
      address: composeManualOpenBankingAddress(parsedAddress) || prev.address
    }))
    setIsManualAddressEntry(true)
  }

  const enterAutocompleteAddressEntry = (): void => {
    setAddressSuggestionList([])
    setIsManualAddressEntry(false)
  }

  const updateManualAddress = (patch: Partial<ManualOpenBankingAddress>): void => {
    setManualAddress(prev => {
      const nextAddress = {
        ...prev,
        ...patch
      }
      const address = composeManualOpenBankingAddress(nextAddress)
      setUserKycData(prevKycData => ({
        ...prevKycData,
        address
      }))

      return nextAddress
    })
  }

  const validateOpenBankingBilling = (): boolean => {
    const nextErrors: OpenBankingValidationErrors = {}

    if (userKycData.name.trim().length === 0) {
      nextErrors.name = 'Enter full name.'
    }

    if (userKycData.dateOfBirth.trim().length === 0) {
      nextErrors.dateOfBirth = 'Enter date of birth.'
    }

    if (userKycData.countryOfResidence.trim().length === 0) {
      nextErrors.countryOfResidence = 'Select country.'
    }

    if (isManualAddressEntry) {
      if (manualAddress.addressLine1.trim().length === 0) {
        nextErrors.addressLine1 = 'Enter address line 1.'
      }

      if (manualAddress.townOrCity.trim().length === 0) {
        nextErrors.townOrCity = 'Enter city or town.'
      }
    } else if (userKycData.address.trim().length === 0) {
      nextErrors.address = 'Enter address.'
    }

    if (userKycData.phoneNumber.trim().length === 0) {
      nextErrors.phoneNumber = 'Enter phone number.'
    } else if (!isValidPhone(userKycData.phoneNumber)) {
      nextErrors.phoneNumber = 'Enter a valid phone number.'
    }

    const firstValidationField = Object.keys(nextErrors)[0] as OpenBankingValidationField | undefined
    setValidationErrors(nextErrors)

    if (firstValidationField) {
      focusValidationField(firstValidationField)
      return false
    }

    return true
  }

  const composeAddressText = (details: AddressSuggestionDetails | null, fallbackAddress: string): string => {
    if (!details) {
      return fallbackAddress
    }

    const locality = [details.townOrCity, details.stateOrProvince]
      .map(part => part?.trim())
      .filter((part): part is string => Boolean(part && part.length > 0))
      .join(', ')

    const parts = [
      details.addressLine1,
      details.addressLine2,
      locality,
      details.postalCode
    ]
      .map(part => part?.trim())
      .filter((part): part is string => Boolean(part && part.length > 0))

    if (parts.length > 0) {
      return parts.join(', ')
    }

    return details.formattedAddress ?? fallbackAddress
  }

  const [isLoading, setIsLoading] = useState(false)

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            justifyItems: 'center',
            alignItems: 'center',
            marginTop: 10,
            marginRight: 5,
            fontSize: 20,
            fontWeight: 600
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {
              renderState !== OpenBankingRenderStates.onramp && (
                <Chevron style={{
                  transform: 'rotate(90deg)',
                  cursor: 'pointer',
                  userSelect: 'none',
                  color: RADOM_COLORS.GRAY_DARKEST
                }}
                onClick={onBack} width={14} height={14} color={RADOM_COLORS.GRAY_DARKEST}
                />
              )
            }
            {
              renderState === OpenBankingRenderStates.onramp
                ? <div>{t('amountToBePaid')}</div>
                : renderState === OpenBankingRenderStates.billing
                  ? <div>{t('billingInformation')}</div>
                  : <div>{t('checkoutSummary')}</div>
            }
          </div>

          {
            renderState === OpenBankingRenderStates.onramp && (
              <div>{formatCurrency(currency, totalSum)}</div>
            )
          }
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            justifyItems: 'center',
            alignItems: 'center',
            marginTop: 4,
            marginRight: 5,
            fontSize: 14,
            color: 'white',
            fontWeight: 400
          }}>
          {
            renderState === OpenBankingRenderStates.onramp && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                alignItems: 'center',
                color: RADOM_COLORS.GRAY_DARKEST,
                width: '100%',
                padding: '0'
              }}>
                <OnrampComponent
                  style={{ marginTop: 35, marginBottom: 2.5 }}
                  method={fromMethod}
                  tag={t('youSpend')}
                  amount={totalSum.toString()}
                  loading={isPreProcessingPayment}
                />
                <div style={{
                  margin: '-26px',
                  width: 40,
                  height: 40,
                  background: 'white',
                  zIndex: 1,
                  borderRadius: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <LinkArrow style={{ transform: 'rotate(90deg)', width: 24 }} stroke={RADOM_COLORS.GRAY_DARKER} />
                </div>
                <OnrampComponent
                  style={{ marginTop: 2.5 }}
                  method={toMethod}
                  tag={t('youReceive')}
                  amount={onrampAmountText ?? ''}
                  placeholder={hasOnrampAmount ? undefined : NO_AMOUNT}
                  loading={isPreProcessingPayment}
                />
                {
                  !hasOnrampAmount &&
                  <div style={{ width: '100%', fontSize: 13, color: RADOM_COLORS.ERROR, textAlign: 'center' }}>
                    {t('priceUnavailable')}
                  </div>
                }
                <div
                  style={{
                    marginTop: 10,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10
                  }}>
                  <div style={{
                    color: RADOM_COLORS.BLACK,
                    marginLeft: 10,
                    marginBottom: 5,
                    fontSize: 14,
                    fontWeight: 500
                  }}>
                    {t('paymentMethod')}
                  </div>
                  <div
                    style={{
                      padding: 20,
                      backgroundColor: RADOM_COLORS.GRAY_LIGHTEST,
                      borderRadius: 20,
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                    <div>
                      {t('instantBankTransfer')}
                    </div>
                    <img src={tBank()} style={{ height: 20, width: 'auto' }} />
                  </div>
                </div>
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10
                  }}>
                  <div style={{
                    color: RADOM_COLORS.BLACK,
                    marginLeft: 10,
                    marginBottom: 5,
                    fontSize: 14,
                    fontWeight: 500
                  }}>
                    {t('receivingAddress')}
                  </div>
                  <div
                    style={{
                      padding: 20,
                      backgroundColor: RADOM_COLORS.GRAY_LIGHTEST,
                      borderRadius: 20,
                      width: '100%',
                      display: 'flex',
                      overflowX: 'auto',
                      scrollbarWidth: 'none'
                    }}>
                    <div>
                      {checkoutData?.gateway.openBanking?.destinationCryptoAddress ?? randomAddress}
                    </div>
                  </div>
                </div>
                <PrimaryButton
                  style={{ width: '100%', marginTop: 20 }}
                  disabled={!hasOnrampAmount}
                  onClick={() => {
                    setRenderState(
                      hasCompleteOpenBankingCustomerDetails(
                        userKycData,
                        checkoutData?.gateway.openBanking?.email
                      )
                        ? OpenBankingRenderStates.summary
                        : OpenBankingRenderStates.billing
                    )
                  }}
                >
                  {t('continueToCheckout')}
                </PrimaryButton>
              </div>

            )
          }

          {
            renderState === OpenBankingRenderStates.billing && (
              <div style={{
                height: '100%',
                minHeight: '475px',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                color: RADOM_COLORS.BLACK,
                fontSize: 14,
                marginTop: 10
              }}>
                <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', justifyContent: 'space-between', width: '100%' }}>
                  <div
                    data-open-banking-field="name"
                    style={{ fontSize: 14, display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}
                  >
                    <TextInputWithLabel
                      label={t('fullName')}
                      value={userKycData.name}
                      required={true}
                      inputStyle={fieldErrorStyle('name')}
                      validationError={validationErrors.name}
                      onChange={(e) => {
                        clearValidationErrors('name')
                        onChangeFullName(e)
                      }}
                    />
                  </div>

                  <div style={{ fontSize: 14, display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
                    <div data-open-banking-field="dateOfBirth" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <InputLabel>
                        <span>{t('dateOfBirth')}</span>
                      </InputLabel>
                      <div style={validationErrors.dateOfBirth
                        ? { border: '1px solid #df1b41', borderRadius: 8, ...INPUT_ERROR_STYLE }
                        : undefined}
                      >
                        <DateSelector
                          placeholder='DD/MM/YYYY'
                          value={userKycData.dateOfBirth ? new Date(userKycData.dateOfBirth) : undefined}
                          onChange={(e) => {
                            clearValidationErrors('dateOfBirth')
                            setUserKycData({ ...userKycData, dateOfBirth: e?.toDateString() ?? '' })
                          }}
                          startDate={new Date('1935-01-01')}
                          endDate={subYears(new Date(), 18)}
                        />
                      </div>
                      {validationErrors.dateOfBirth && (
                        <InlineInputError>{validationErrors.dateOfBirth}</InlineInputError>
                      )}
                    </div>
                  </div>
                </div>
                <TextInputWithLabel
                  label={t('email')}
                  disabled={true}
                  value={checkoutData?.gateway.openBanking?.email ?? ''}
                  required={true}
                  onChange={() => { }}
                />

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
                  <InputLabel>{t('countryoOfResidence')}</InputLabel>
                  <div data-open-banking-field="countryOfResidence">
                    <CountrySelector
                      allowedCountries={allCountries}
                      style={fieldErrorStyle('countryOfResidence')}
                      selectedCountry={
                        (
                          allCountries.find(
                            c =>
                              c.name.toLowerCase() === userKycData.countryOfResidence.toLowerCase() ||
                              c.isoCode.toLowerCase() === userKycData.countryOfResidence.toLowerCase()
                          )?.name ?? userKycData.countryOfResidence
                        ) || ''
                      }
                      onCountrySelected={country => {
                        if (country) {
                          clearValidationErrors('countryOfResidence')
                          setUserKycData(prev => ({
                            ...prev,
                            phoneNumber: prev.phoneNumber.trim().length === 0 ? `+${country.phonecode} ` : prev.phoneNumber,
                            countryOfResidence: country.name
                          }))
                        }
                      }}
                    />
                  </div>
                  {validationErrors.countryOfResidence && (
                    <InlineInputError>{validationErrors.countryOfResidence}</InlineInputError>
                  )}
                </div>
                <>
                  {!isManualAddressEntry && (
                    <div data-open-banking-field="address" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <TextInputWithLabelWithSuggestions
                        label={t('address')}
                        value={userKycData.address}
                        placeholder={t('address')}
                        required={true}
                        isInvalid={Boolean(validationErrors.address)}
                        validationError={validationErrors.address}
                        suggestionList={addressSuggestionList}
                        setSelectedItems={(items) => {
                          const selectedAddress = items[0]
                          if (!selectedAddress) {
                            return
                          }

                          setAddressSuggestionList([])
                          clearValidationErrors('address', 'addressLine1', 'townOrCity')
                          setUserKycData(prev => ({ ...prev, address: selectedAddress }))

                          void (async () => {
                            let details: AddressSuggestionDetails | null = null
                            try {
                              const selectedCountry = allCountries.find(c => c.name === userKycData.countryOfResidence)
                              details = await Radom.getGooglePlaceAddressDetails(
                                selectedAddress,
                                selectedCountry?.isoCode ?? ''
                              )
                            } catch (err) {
                              console.error('Failed to resolve selected address details', err)
                              details = null
                            }

                            if (!details) {
                              return
                            }

                            const countryFromDetails = details.country?.toLowerCase()
                            const matchedCountry = allCountries.find(
                              c => c.name.toLowerCase() === countryFromDetails ||
                                c.isoCode.toLowerCase() === countryFromDetails
                            )
                            const resolvedAddress = composeAddressText(details, selectedAddress)

                            setUserKycData(prev => ({
                              ...prev,
                              address: resolvedAddress,
                              countryOfResidence:
                                prev.countryOfResidence.trim().length > 0
                                  ? prev.countryOfResidence
                                  : (matchedCountry?.name ?? prev.countryOfResidence),
                              phoneNumber: (matchedCountry && prev.phoneNumber.trim().length === 0)
                                ? `+${matchedCountry.phonecode} `
                                : prev.phoneNumber
                            }))
                          })()
                        }}
                        onChange={(e) => {
                          clearValidationErrors('address', 'addressLine1', 'townOrCity')
                          const value = (e.target as HTMLInputElement).value
                          if (value.length === 0) {
                            setAddressSuggestionList([])
                          }

                          setUserKycData(prev => ({
                            ...prev,
                            address: value
                          }))

                          if (value.length > 2) {
                            const country = allCountries.find(
                              (c) => c.name === userKycData.countryOfResidence
                            )
                            debouncedFetch(value, country?.isoCode ?? '', setAddressSuggestionList)
                          } else {
                            setAddressSuggestionList([])
                          }
                        }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          onClick={enterManualAddressEntry}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            color: '#97a3b6',
                            textDecoration: 'underline',
                            textUnderlineOffset: 2,
                            cursor: 'pointer',
                            fontSize: 13,
                            padding: '2px 0'
                          }}
                        >
                          Enter address manually
                        </button>
                      </div>
                    </div>
                  )}

                  {isManualAddressEntry && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <InputLabel>{t('address')}</InputLabel>
                      <TextInput
                        name="address-line1"
                        autoComplete="address-line1"
                        value={manualAddress.addressLine1}
                        placeholder="Address line 1"
                        required
                        aria-invalid={Boolean(validationErrors.addressLine1)}
                        data-open-banking-field="addressLine1"
                        style={fieldErrorStyle('addressLine1')}
                        onChange={(e) => {
                          clearValidationErrors('addressLine1')
                          updateManualAddress({ addressLine1: e.currentTarget.value })
                        }}
                      />
                      {validationErrors.addressLine1 && (
                        <InlineInputError>{validationErrors.addressLine1}</InlineInputError>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          onClick={enterAutocompleteAddressEntry}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            color: '#97a3b6',
                            textDecoration: 'underline',
                            textUnderlineOffset: 2,
                            cursor: 'pointer',
                            fontSize: 13,
                            padding: '2px 0'
                          }}
                        >
                          Use address autocomplete
                        </button>
                      </div>
                      <TextInput
                        name="address-line2"
                        autoComplete="address-line2"
                        value={manualAddress.addressLine2}
                        placeholder="Address line 2"
                        onChange={(e) => updateManualAddress({ addressLine2: e.currentTarget.value })}
                      />
                      <div style={{ display: 'flex', gap: 10 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                          <TextInput
                            name="address-level2"
                            autoComplete="address-level2"
                            value={manualAddress.townOrCity}
                            placeholder="City / Town"
                            required
                            aria-invalid={Boolean(validationErrors.townOrCity)}
                            data-open-banking-field="townOrCity"
                            style={fieldErrorStyle('townOrCity')}
                            onChange={(e) => {
                              clearValidationErrors('townOrCity')
                              updateManualAddress({ townOrCity: e.currentTarget.value })
                            }}
                          />
                          {validationErrors.townOrCity && (
                            <InlineInputError>{validationErrors.townOrCity}</InlineInputError>
                          )}
                        </div>
                        <TextInput
                          name="postal-code"
                          autoComplete="postal-code"
                          value={manualAddress.postalCode}
                          placeholder="Postcode"
                          onChange={(e) => updateManualAddress({ postalCode: e.currentTarget.value })}
                        />
                      </div>
                      <TextInput
                        name="address-level1"
                        autoComplete="address-level1"
                        value={manualAddress.stateOrProvince}
                        placeholder="State / Region"
                        onChange={(e) => updateManualAddress({ stateOrProvince: e.currentTarget.value })}
                      />
                    </div>
                  )}

                  <div data-open-banking-field="phoneNumber">
                    <TextInputWithLabel
                      label={t('phoneNumber')}
                      required
                      value={userKycData.phoneNumber}
                      inputStyle={fieldErrorStyle('phoneNumber')}
                      validationError={validationErrors.phoneNumber}
                      onChange={(e) => {
                        clearValidationErrors('phoneNumber')
                        setUserKycData(prev => ({ ...prev, phoneNumber: (e.target as HTMLInputElement).value }))
                      }}
                    />
                  </div>
                </>

                <div style={{ position: 'sticky', bottom: 0, background: 'white', paddingTop: 20, zIndex: 2 }}>
                  <PrimaryButton
                    style={{ width: '100%', marginTop: 24 }}
                    onClick={() => {
                      if (!validateOpenBankingBilling()) {
                        return
                      }
                      setErrorMessage('')
                      trackOpenBankingStep('travelRuleCompleted')
                      setRenderState(OpenBankingRenderStates.summary)
                    }}
                  >
                    {t('continueToSummary')}
                  </PrimaryButton>
                </div>
              </div>
            )
          }

          {
            renderState === OpenBankingRenderStates.summary && (
              <div style={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 24,
                color: RADOM_COLORS.BLACK,
                fontSize: 14,
                marginTop: 10
              }}>

                <div
                  style={{
                    width: '100%',
                    padding: 24,
                    borderRadius: 20,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    gap: 16
                  }}
                >

                  <div style={{ fontSize: 13, color: '#6B6C7E' }}>
                    {"You're paying"}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      gap: 8
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                      <PaymentMethodDisplay
                        size={28}
                        method={fromMethod}
                        excludeName
                      />
                      <span style={{ fontSize: 34, fontWeight: 600 }}>
                        {Number(totalSum).toFixed(2)} {currency}
                      </span>
                    </div>
                  </div>
                  <div style={{ fontSize: 13, color: '#6B6C7E' }}>
                    Including all fees
                  </div>
                  <div
                    style={{
                      height: 1.5,
                      backgroundColor: RADOM_COLORS.GRAY_DARKEST,
                      width: '100%',
                      opacity: 0.1
                    }}
                  ></div>
                </div>

                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                      width: '100%'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        // lineHeight: '28px',
                        marginBottom: 14
                      }}
                    >
                      <div style={{ fontSize: 16, color: '#6B6C7E', fontWeight: 500 }}>
                        {t('estTime')}
                      </div>
                      <div style={{ fontWeight: 500, fontSize: 14 }}>
                        {t('INSTANT')}
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start'
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          color: '#6B6C7E',
                          lineHeight: '20px'
                        }}
                      >
                        {t('youReceive')}
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                          gap: 4
                        }}
                      >
                        <div style={{ lineHeight: '18px', fontWeight: 200 }}>
                          {onrampAmountText ?? NO_AMOUNT}
                          <span
                            style={{ fontSize: 10, color: RADOM_COLORS.GRAY_DARKEST }}> USDC </span>
                        </div>

                        <div style={{ fontSize: 8, color: '#6B6C7E' }}>
      on Ethereum
                        </div>
                      </div>
                    </div>

                  </div>

                  <div
                    style={{
                      padding: 16,
                      backgroundColor: RADOM_COLORS.GRAY_LIGHTEST,
                      borderRadius: 16,
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 12,
                      flexDirection: 'column',
                      overflowX: 'auto',
                      scrollbarWidth: 'none',
                      marginTop: 10,
                      opacity: 0.6
                    }}
                  >
                    <div
                      style={{
                        color: RADOM_COLORS.GRAY_DARKEST,
                        fontSize: 14
                      }}
                    >
                      {t('receivingAddress')}
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{ fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all' }}>
                        {checkoutData?.gateway.openBanking?.destinationCryptoAddress ?? randomAddress}
                      </div>
                      <Edit
                        stroke={RADOM_COLORS.GRAY_DARKER}
                        style={{ height: 16, width: 'auto', cursor: 'default' }}
                      />
                    </div>

                  </div>
                </div>

                <div style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                  marginTop: 40
                }}>
                  <div
                    style={{
                      marginLeft: 5,
                      fontSize: 11,
                      lineHeight: 1.5,
                      color: RADOM_COLORS.GRAY_DARKEST
                    }}
                  >
                    By continuing, you agree to our{' '}
                    <span
                      style={{ color: RADOM_COLORS.NEW_BLUE, cursor: 'pointer' }}
                      onClick={() => window.open('https://www.radom.com/terms', '_blank')}
                    >
                      Terms and Conditions
                    </span>{' '}
                    and{' '}
                    <span
                      style={{ color: RADOM_COLORS.NEW_BLUE, cursor: 'pointer' }}
                      onClick={() => window.open('https://www.radom.com/acceptable-use-policy', '_blank')}
                    >
                      Acceptable Use Policy
                    </span>
                    . You confirm that you are not purchasing crypto assets for investment purposes and understand
                    the risks involved in digital asset transactions.
                  </div>
                  {
                    !hasOnrampAmount &&
                    <div style={{ fontSize: 13, color: RADOM_COLORS.ERROR, textAlign: 'center' }}>
                      {t('priceUnavailable')}
                    </div>
                  }
                  <PrimaryButton
                    disabled={isLoading || !hasOnrampAmount}
                    onClick={() => {
                      trackOpenBankingStep('paymentInitiated')
                      setIsLoading(true)
                      localStorage.setItem('OpenBankingAprove', 'true')

                      if (!isSecureHostedUrl(checkoutData?.gateway.openBanking?.openBankingCheckoutUrl)) {
                        Radom.updateCustomerKycData(
                          checkoutId ?? '',
                          buildOpenBankingCustomerPayload(userKycData)
                        )
                          .then(async () => {
                            const res = await Radom.getCheckoutSession(checkoutId ?? '')
                            const checkoutUrl = res.gateway.openBanking?.openBankingCheckoutUrl
                            if (!checkoutUrl) {
                              setOpenbankPayStatus(PaymentStatus.KYC)
                            } else {
                              setOpenBankingCheckoutUrl(checkoutUrl)
                              setOpenbankPayStatus(PaymentStatus.WAITING)
                            }
                            setIsLoading(false)
                          })
                          .catch((e) => {
                            setErrorMessage(e.message || '')
                            setIsLoading(false)
                          })
                      } else {
                        setIsLoading(false)
                        setOpenbankPayStatus(PaymentStatus.WAITING)
                      }
                    }}
                    style={{
                      marginTop: 20,
                      width: '100%',
                      minHeight: 52,
                      borderRadius: 14,
                      fontSize: 16,
                      fontWeight: 500,
                      backgroundColor: RADOM_COLORS.BLUE4
                    }}
                  >
                    {isLoading
                      ? (
                        <Spinner />
                      )
                      : (
                        <span>{t('agreeAndContinue')}</span>
                      )}
                  </PrimaryButton>
                </div>
              </div>
            )
          }

        </div>
      </div>

      {
        errorMessage && errorMessage.length > 0 &&
          <div style={{
            backgroundColor: RADOM_COLORS.ERROR,
            padding: 15,
            borderRadius: 5,
            marginBottom: 20,
            color: 'white',
            overflowWrap: 'break-word'
          }}>
            {errorMessage}
          </div>
      }

      <div style={{ display: 'flex', justifyContent: 'center', width: '100%', fontSize: 12 }}>

        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>

          <a
            href='https://www.radom.com/industries/igaming'
            target='_blank'
            title='Crypto Presale Powered by Radom'
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              cursor: 'pointer',
              userSelect: 'none',
              textDecoration: 'none',
              color: RADOM_COLORS.GRAY_DARKEST
            }} rel="noreferrer"
          >
            <span style={{ display: 'flex', alignItems: 'center', fontSize: 13, fontWeight: 500 }}>
                Powered by
              <RadomLogo
                width={60}
                height={14}
                fill={RADOM_COLORS.GRAY_DARKEST}
              />
            </span>
            <span style={{ position: 'absolute', width: '1px', height: '1px', margin: '-1px', padding: 0, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', border: 0 }}>
                Radom
            </span>
          </a>
        </div>
      </div>
    </>

  )
}

const HostedCheckout = (): React.ReactElement => {
  const { checkoutId } = useParams()
  const [searchParams] = useSearchParams()

  const posMode = useMemo(() => searchParams.get('posMode'), [])
  const isOpenBankingRedirectReturn = searchParams.get('forceRedirect') === 'true'

  const [isLoading, setIsLoading] = useState(true)
  const [isPreProcessingPayment, setIsPreProcessingPayment] = useState(false)

  const [isExpired, setIsExpired] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [successElement, setSuccessElement] = useState<ReactElement>(<></>)
  const [loadError, setLoadError] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [totalSum, setTotalSum] = useState(0)
  const [currency, setCurrency] = useState(DEFAULT_FIAT_CURRENCY)

  const [checkoutData, setCheckoutData] = useState<CheckoutData>()
  const [addressSuggestionList, setAddressSuggestionList] = useState<string[]>([])

  const [managedPaymentDetails, setManagedPaymentDetails] = useState<IManagedPaymentMethodDetails>()
  const [
    tokenDelegatePaymentDetails,
    setTokenDelegatePaymentDetails
  ] = useState<IManagedTokenDelegatePaymentMethodDetails>()

  const [discountCode, setDiscountCode] = useState('')
  const [discountResult, setDiscountResult] = useState({ success: false, element: <></> })
  const [discount, setDiscount] = useState<IDiscountCode>()
  const [discountLocked, setDiscountLocked] = useState(false)

  const [paymentStatus, setPaymentStatus] = useState<IManagedPaymentStatus>()
  const managedPollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const trackedFunnelStepsRef = useRef<Set<string>>(new Set())
  const trackHostedCheckoutStep = useCallback((
    rail: 'crypto' | 'openBanking',
    step:
      | 'viewed'
      | 'travelRuleCompleted'
      | 'paymentInitiated'
      | 'bankRedirectReturned'
      | 'paymentConfirmed'
      | 'paymentFailed',
    metadata?: Record<string, unknown>
  ) => {
    if (!checkoutId) {
      return
    }

    const dedupeKey = `${rail}:${step}`
    if (trackedFunnelStepsRef.current.has(dedupeKey)) {
      return
    }

    trackedFunnelStepsRef.current.add(dedupeKey)
    void trackCheckoutFunnelStep({
      sourceType: 'checkoutSession',
      sourceId: checkoutId,
      rail,
      step,
      metadata
    })
  }, [checkoutId])

  const [isOpenbank, setIsOpenbank] = useState(false)

  const [openbankPayStatus, setOpenbankPayStatus] = React.useState(PaymentStatus.INITIAL)
  const openBankingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const openBankingReturnIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const allowCountries = [
    'Austria',
    'Belgium',
    'Bulgaria',
    'Croatia',
    'Cyprus',
    'Czech Republic',
    'Denmark',
    'Estonia',
    'Finland',
    'France',
    'Germany',
    'Greece',
    'Hungary',
    'Iceland',
    'Ireland',
    'Italy',
    'Latvia',
    'Liechtenstein',
    'Lithuania',
    'Luxembourg',
    'Malta',
    'Netherlands',
    'Norway',
    'Poland',
    'Portugal',
    'Romania',
    'Slovakia',
    'Slovenia',
    'Spain',
    'Sweden',
    'United Kingdom'
  ]
  const allCountries = Country.getAllCountries().filter((c) => {
    return allowCountries.includes(c.name)
  })

  const [userKycData, setUserKycData] = useState<kycData>({
    name: '',
    phoneNumber: '',
    countryOfResidence: '',
    address: '',
    dateOfBirth: '',
    passportFile: '',
    idCardFile: '',
    selfieFile: ''
  })

  const [checkMarkOpenbanking, setCheckMarkOpenBanking] = useState(false)

  const randomAddress = useMemo(() =>
    '0x' + [...Array(40)].map(() => '0123456789abcdef'[Math.floor(Math.random() * 16)]).join(''), []
  )

  const [openBankingCheckoutUrl, setOpenBankingCheckoutUrl] = useState('')
  const hasOpenBankingCheckout = !!checkoutData?.gateway.openBanking
  const openBankingReturnStatusCopy = getOpenBankingReturnStatusCopy(
    checkoutData?.openBankingPaymentData,
    checkoutData?.sessionStatus
  )
  const debouncedFetch = useRef(
    _.debounce((value: string, countryCode: string, setAddressSuggestionList) => {
      Radom.getGoolgePlaceAutoComplete(value, countryCode).then((res) => {
        setAddressSuggestionList(res)
      })
    }, 500)
  ).current

  // Start by assuming that none of the travel rule fields have been collected
  const [travelRuleFieldsCollected, setTravelRuleFieldsCollected] = useState(zeroTravelRuleFieldsCollected())

  useEffect(() => {
    if (!checkoutData?.gateway.openBanking) {
      return
    }

    if (openBankingIntervalRef.current) {
      clearInterval(openBankingIntervalRef.current)
    }

    if (checkoutData?.sessionStatus === 'success' || checkoutData?.sessionStatus === 'refunded') {
      setOpenbankPayStatus(PaymentStatus.SUCCESS)
      return
    }

    // Past its expiry time, a session with a bank payment in flight behaves as
    // it did before the expiry: the payment is still on its way.
    if (
      checkoutData?.sessionStatus === 'expired' &&
      !isOpenBankingPaymentInFlight(checkoutData.openBankingPaymentData)
    ) {
      setOpenbankPayStatus(PaymentStatus.Expired)
      return
    }

    if (isOpenBankingProviderFailed(checkoutData.openBankingPaymentData)) {
      setErrorMessage(openBankingReturnStatusCopy.body)
      setOpenbankPayStatus(PaymentStatus.ERROR)
      return
    }

    if (isOpenBankingRedirectReturn) {
      setOpenbankPayStatus(PaymentStatus.KYC_PENDING_VIEW)
      setRenderOpenBankingState(OpenBankingRenderStates.summary)
      setIsLoading(false)
      return
    }

    if (checkoutData?.gateway.openBanking?.openBankingCheckoutUrl && checkoutData?.gateway.openBanking?.openBankingCheckoutUrl !== '') {
      if (isSecureHostedUrl(checkoutData?.gateway.openBanking?.openBankingCheckoutUrl)) {
        setOpenbankPayStatus(PaymentStatus.KYC_PENDING_VIEW)
        const openBankingClicked = localStorage.getItem('OpenBankingAprove')
        setTimeout(() => {
          if (checkoutData?.gateway.openBanking?.existingUserPaidSuccess === true || openBankingClicked) {
            setOpenbankPayStatus(PaymentStatus.WAITING)
          } else {
            setOpenbankPayStatus(PaymentStatus.INITIAL)
          }
          setRenderOpenBankingState(OpenBankingRenderStates.summary)
        }, 2000)
      } else {
        setErrorMessage(formatOpenBankingErrorMessage(checkoutData?.gateway.openBanking?.openBankingCheckoutUrl))
        setOpenbankPayStatus(PaymentStatus.ERROR)
      }
    }

    setIsLoading(false)
  }, [
    checkoutData?.gateway.openBanking,
    checkoutData?.openBankingPaymentData,
    checkoutData?.sessionStatus,
    isOpenBankingRedirectReturn,
    openBankingReturnStatusCopy.body
  ])

  useEffect(() => {
    if (openbankPayStatus === PaymentStatus.SUCCESS) {
      trackHostedCheckoutStep('openBanking', 'paymentConfirmed', {
        sessionStatus: checkoutData?.sessionStatus,
        openBankingStatus: checkoutData?.openBankingPaymentData?.status,
        redirectReturn: isOpenBankingRedirectReturn
      })
    }
  }, [
    checkoutData?.openBankingPaymentData?.status,
    checkoutData?.sessionStatus,
    isOpenBankingRedirectReturn,
    openbankPayStatus,
    trackHostedCheckoutStep
  ])

  useEffect(() => {
    if (openbankPayStatus === PaymentStatus.ERROR) {
      trackHostedCheckoutStep('openBanking', 'paymentFailed', {
        sessionStatus: checkoutData?.sessionStatus,
        openBankingStatus: checkoutData?.openBankingPaymentData?.status,
        redirectReturn: isOpenBankingRedirectReturn,
        reason: errorMessage || openBankingReturnStatusCopy.body
      })
    }
  }, [
    checkoutData?.openBankingPaymentData?.status,
    checkoutData?.sessionStatus,
    errorMessage,
    isOpenBankingRedirectReturn,
    openBankingReturnStatusCopy.body,
    openbankPayStatus,
    trackHostedCheckoutStep
  ])

  const getCheckoutData = async (): Promise<void> => {
    if (!checkoutId) {
      setLoadError('Checkout session ID is missing.')
      return
    }

    setLoadError('')
    try {
      const processCheckoutSession = (checkoutSession: CheckoutSession): CheckoutSession => ({
        ...checkoutSession,
        successUrl: checkoutSession.successUrl
          ? resolveNavigationUrl(
            checkoutSession.successUrl.replace('{CHECKOUT_SESSION_ID}', checkoutSession.id)
          )
          : '',
        cancelUrl: resolveNavigationUrl(
          checkoutSession.cancelUrl?.replace('{CHECKOUT_SESSION_ID}', checkoutSession.id)
        )
      })

      const c = processCheckoutSession(
        await Radom.getCheckoutSession(checkoutId, {
          refreshOpenBankingStatus: isOpenBankingRedirectReturn
        })
      )
      const p = await Radom.getSellerProfile(c.organizationId)
      trackHostedCheckoutStep(c.gateway.openBanking ? 'openBanking' : 'crypto', 'viewed')

      // Which travel rule fields have already been collected?
      setTravelRuleFieldsCollected({
        countryOfResidenceCollected: c.travelRuleFields.countryOfResidenceCollected,
        dateOfBirthCollected: c.travelRuleFields.dateOfBirthCollected,
        emailAddressCollected: c.travelRuleFields.emailAddressCollected,
        nameCollected: c.travelRuleFields.nameCollected,
        companyNameCollected: c.travelRuleFields.companyNameCollected,
        accountType: c.travelRuleFields.accountType,
        addressLine1Collected: c.travelRuleFields.addressLine1Collected,
        addressLine2Collected: c.travelRuleFields.addressLine2Collected,
        townOrCityCollected: c.travelRuleFields.townOrCityCollected,
        postalCodeCollected: c.travelRuleFields.postalCodeCollected,
        stateOrProvinceCollected: c.travelRuleFields.stateOrProvinceCollected
      })

      const cData = {
        ...c,
        total: c.total !== undefined && c.total !== null ? Number(c.total) : undefined,
        sellerLogoUrl: p?.logoUrl ?? '',
        sellerName: p?.name ?? c.sellerAddress,
        products: c.products?.map(p => { return { product: p, quantity: 1 } }) ?? [],
        lineItems: c.items
      }
      setCheckoutData(cData as any)

      if ((c.metadata ?? []).filter((m: any) => m.key === 'locale').length > 0) {
        setLanguage((c.metadata?.find((m: any) => m.key === 'locale')?.value as Language) ?? 'en')
      } else {
        setLanguage('en')
      }

      if (c.gateway.openBanking) {
        setIsOpenbank(true)
        const openBankingGateway = c.gateway.openBanking
        const kycData = openBankingGateway.kycData
        const nextUserKycData = {
          name: kycData?.name ?? '',
          phoneNumber: kycData?.phone ?? '',
          countryOfResidence: openBankingGateway.residenceCountry ?? '',
          address: kycData?.address ?? '',
          dateOfBirth: kycData?.dob ?? '',
          passportFile: '',
          idCardFile: '',
          selfieFile: ''
        }

        setUserKycData(nextUserKycData)
        setOpenBankingCheckoutUrl(openBankingGateway.openBankingCheckoutUrl ?? '')

        if (hasCompleteOpenBankingCustomerDetails(nextUserKycData, openBankingGateway.email)) {
          setRenderOpenBankingState(OpenBankingRenderStates.summary)
        } else if (!openBankingGateway.destinationCryptoAddress) {
          setRenderOpenBankingState(OpenBankingRenderStates.billing)
        }
      }

      const products: ProductWithQuantity[] = []
      if (c.products) {
        c.products.forEach(p => {
          products.push({
            product: {
              ...p,
              chargingIntervalSeconds: p.chargingIntervalSeconds || 0
            },
            quantity: 1
          })
        })
      }
      if (c.items) {
        c.items.forEach(lineItem => {
          const lineProduct = {
            id: '',
            name: lineItem.name,
            description: lineItem.description ?? '',
            chargingIntervalSeconds: lineItem.chargingIntervalSeconds || 0,
            price: lineItem.price,
            currency: lineItem.currency ?? DEFAULT_FIAT_CURRENCY,
            createdAt: new Date(),
            imageUrl: lineItem.imageUrl,
            updatedAt: new Date(),
            sellerAddress: checkoutData?.sellerAddress ?? ''
          }
          products.push({
            product: lineProduct as any,
            quantity: 1
          })
        })
      }

      const hasPaymentInFlight = !!checkoutId && !!loadPaymentSession(checkoutId)
      if (
        c.expiresAt && (new Date(c.expiresAt) < new Date()) && !hasPaymentInFlight &&
        !isOpenBankingPaymentInFlight(c.openBankingPaymentData)
      ) {
        setIsExpired(true)
      }

      if (c.sessionStatus === 'cancelled') {
        if (c.cancelUrl && !c.gateway.openBanking) {
          setTimeout(() => { window.location.href = resolveNavigationUrl(c.cancelUrl) }, 2000)
        }
      }

      if (c.sessionStatus === 'success' || c.sessionStatus === 'refunded') {
        setIsComplete(true)
        if (c.successUrl && !c.gateway.openBanking) {
          setTimeout(() => { window.location.href = resolveNavigationUrl(c.successUrl) }, 3000)
        } else if (c.gateway.openBanking) {
          if (c.successUrl && c.gateway.openBanking.destinationCryptoAddress === null) {
            setTimeout(() => { window.location.href = resolveNavigationUrl(c.successUrl) }, 3000)
          }
        }
      }

      if (Number(c.total) > 0) {
        setTotalSum(Number(c.total))
        setCurrency(c.currency ?? DEFAULT_FIAT_CURRENCY)
      } else {
        let primaryCurrency = DEFAULT_FIAT_CURRENCY
        const subscriptionProducts = products.filter(product => product.product.chargingIntervalSeconds > 0)
        if (subscriptionProducts.length > 0) {
          primaryCurrency = subscriptionProducts[0]?.product.currency ?? DEFAULT_FIAT_CURRENCY
        } else if (cData.products && cData.products.length > 0) {
          primaryCurrency = cData.products?.[0]?.product?.currency ?? DEFAULT_FIAT_CURRENCY
        }
        setCurrency(primaryCurrency)
        const productCurrencies = [
          ...cData.items?.map(i => i.currency ?? DEFAULT_FIAT_CURRENCY),
          ...cData.products?.map(p => p.product.currency)
        ]
        try {
          const quotes = await Radom.getPriceQuotes(productCurrencies, [primaryCurrency])
          const [total] = getTotalSum(cData as any, quotes, primaryCurrency)
          setTotalSum(total)
        } catch (err) {
          console.error('Failed to load checkout price quotes', err)
        }
      }
    } catch (err: any) {
      console.error('Failed to load checkout session', err)
      setLoadError(err?.response?.data?.error ?? err?.message ?? 'Unable to load checkout right now.')
    }
  }

  const getPriceQuotes = (): void => {
    if (!checkoutData) {
      return
    }

    const normalizedCheckoutTotal = Number(checkoutData.total ?? 0)
    const hasProvidedTotal =
      Number.isFinite(normalizedCheckoutTotal) && normalizedCheckoutTotal > 0
    const productCurrencies = [
      ...checkoutData.lineItems?.map(i => i.currency ?? DEFAULT_FIAT_CURRENCY) ?? [DEFAULT_FIAT_CURRENCY],
      ...checkoutData.products?.map(p => p.product.currency) ?? [DEFAULT_FIAT_CURRENCY]
    ]

    if (productCurrencies.length === 0) {
      if (hasProvidedTotal) {
        setTotalSum(normalizedCheckoutTotal)
      }
      return
    }

    Radom.getPriceQuotes(productCurrencies, [currency])
      .then(p => {
        // Get price quotes in terms of first product's currency
        const [total] = getTotalSum(checkoutData, p, currency)
        const normalizedTotal = Number(total)
        setTotalSum(
          Number.isFinite(normalizedTotal) && normalizedTotal > 0
            ? normalizedTotal
            : hasProvidedTotal
              ? normalizedCheckoutTotal
              : 0
        )
      })
      .catch(err => {
        console.error('Failed to refresh price quotes', err)
        if (hasProvidedTotal) {
          setTotalSum(normalizedCheckoutTotal)
        }
      })
  }

  const clearManagedPaymentPolling = (): void => {
    if (managedPollingIntervalRef.current) {
      clearInterval(managedPollingIntervalRef.current)
      managedPollingIntervalRef.current = null
    }
  }

  const startManagedPaymentPolling = (managed: IManagedPaymentMethodDetails): void => {
    clearManagedPaymentPolling()
    let appliedOutcome: string | undefined

    managedPollingIntervalRef.current = setInterval(() => {
      Radom.getManagedPaymentStatus(managed.paymentId).then(res => {
        setPaymentStatus(res)

        if (res.status === 'inReview') {
          if (appliedOutcome !== 'inReview') {
            appliedOutcome = 'inReview'
            setIsSuccess(true)
            setSuccessElement(<PaymentUnderReviewMessage />)
          }
          return
        }

        if (res.status === 'paymentConfirmed') {
          appliedOutcome = 'paymentConfirmed'
          trackHostedCheckoutStep('crypto', 'paymentConfirmed')
          if (checkoutId) {
            clearPaymentSession(checkoutId)
          }
          if (checkoutData?.successUrl && !posMode) {
            setTimeout(() => {
              if (checkoutData?.successUrl) {
                window.location.href = resolveNavigationUrl(checkoutData.successUrl)
              }
            }, 3000)
          }
          setIsSuccess(true)
          clearManagedPaymentPolling()
          setSuccessElement(<PaymentSuccessMessage />)
        }

        if (res.status === 'failed' || res.status === 'expired') {
          if (res.status === 'failed') {
            trackHostedCheckoutStep('crypto', 'paymentFailed')
          }
          if (checkoutId) {
            clearPaymentSession(checkoutId)
          }
          clearManagedPaymentPolling()
        }
      }).catch(() => { })
    }, 1000)
  }

  const startTokenDelegatePolling = (paymentId: string): void => {
    clearManagedPaymentPolling()
    let appliedOutcome: string | undefined

    managedPollingIntervalRef.current = setInterval(() => {
      Radom.getManagedTokenPaymentStatus(paymentId).then(res => {
        setPaymentStatus(res)

        if (res.status === 'inReview') {
          if (appliedOutcome !== 'inReview') {
            appliedOutcome = 'inReview'
            setIsPreProcessingPayment(false)
            setIsSuccess(true)
            setSuccessElement(<PaymentUnderReviewMessage />)
          }
          return
        }

        if (res.status === 'paymentConfirmed') {
          appliedOutcome = 'paymentConfirmed'
          trackHostedCheckoutStep('crypto', 'paymentConfirmed')
          setIsPreProcessingPayment(false)
          setIsSuccess(true)
          clearManagedPaymentPolling()
          if (checkoutId) {
            clearPaymentSession(checkoutId)
          }

          setSuccessElement(<PaymentSuccessMessage />)

          setTimeout(() => {
            if (checkoutData?.successUrl) {
              window.location.href = resolveNavigationUrl(checkoutData.successUrl)
            }
          }, 3000)
        }

        if (res.status === 'failed') {
          trackHostedCheckoutStep('crypto', 'paymentFailed')
          clearManagedPaymentPolling()
          if (checkoutId) {
            clearPaymentSession(checkoutId)
          }
          setIsPreProcessingPayment(false)
          setTokenDelegatePaymentDetails(undefined)
          setErrorMessage('Transaction failed, please try again.')
        }
      }).catch(() => { })
    }, 1000)
  }

  useEffect(() => {
    if (!checkoutId || !checkoutData || isComplete || isExpired || managedPollingIntervalRef.current) {
      return
    }

    const session = loadPaymentSession(checkoutId)
    if (!session) {
      return
    }

    if (session.managed) {
      setDiscountLocked(true)
      setManagedPaymentDetails(session.managed)
      startManagedPaymentPolling(session.managed)
      return
    }

    if (session.tokenDelegate && session.delegateSubmitted) {
      setDiscountLocked(true)
      setIsPreProcessingPayment(true)
      setTokenDelegatePaymentDetails(session.tokenDelegate)
      startTokenDelegatePolling(session.tokenDelegate.paymentId)
    }
  }, [checkoutId, checkoutData, isComplete, isExpired])

  useEffect(() => clearManagedPaymentPolling, [])

  const onCheckout = async ({
    managed,
    completeTokenDelegate,
    subscriptionOptions
  }: CheckoutParams): Promise<void> => {
    if (managed) {
      if (!checkoutData) throw new Error('Something went wrong')
      if (!checkoutId) throw new Error('Something went wrong')

      const storedSession = loadPaymentSession(checkoutId)
      if (storedSession?.managed) {
        setDiscountLocked(true)
        setManagedPaymentDetails(storedSession.managed)
        startManagedPaymentPolling(storedSession.managed)
        return
      }

      const { selectedMethod: method } = managed

      let paymentMethod: PaymentMethod = {
        managed: {
          method: {
            network: method.tokenInfo?.networkName ?? method.name,
            token: method.tokenInfo?.tokenAddress ?? undefined
          }
        }
      }

      if (subscriptionOptions) {
        if (subscriptionOptions.autopay) {
          if (!method.tokenInfo) {
            setErrorMessage('Cannot set up an autopay subscription without an EVM based token')
            return
          }

          paymentMethod = {
            managedTokenDelegate: {
              buyerAddress: subscriptionOptions.autopay.address,
              network: method.tokenInfo.networkName,
              token: method.tokenInfo.tokenAddress
            }
          }
        }

        if (subscriptionOptions.emailInvoice) {
          if (!subscriptionOptions.emailInvoice.emailAddress) {
            setErrorMessage('Cannot set up an email invoice subscription without an email address')
            return
          }
        }
      }

      trackHostedCheckoutStep('crypto', 'travelRuleCompleted')
      setIsPreProcessingPayment(true)

      try {
        const res = await Radom.createCheckoutSessionOrder({
          checkoutSessionId: checkoutId,
          paymentMethod,
          orderData: [],
          orderProductData: [],
          emailAddress: subscriptionOptions?.emailInvoice?.emailAddress.toLowerCase(),
          discountCode
        })

        trackHostedCheckoutStep('crypto', 'paymentInitiated', {
          network: method.tokenInfo?.networkName ?? method.name,
          tokenAddress: method.tokenInfo?.tokenAddress
        })

        setDiscountLocked(true)

        if (res.paymentMethodDetails.managed) {
          const managedDetails = res.paymentMethodDetails.managed
          setManagedPaymentDetails(managedDetails)
          if (checkoutId) {
            savePaymentSession(checkoutId, { managed: managedDetails })
          }
          startManagedPaymentPolling(managedDetails)
        } else if (res.paymentMethodDetails.managedTokenDelegate) {
          const delegateDetails = res.paymentMethodDetails.managedTokenDelegate
          setTokenDelegatePaymentDetails(delegateDetails)
          if (checkoutId) {
            savePaymentSession(checkoutId, { tokenDelegate: delegateDetails })
          }
        } else {
          throw new Error('Unexpected empty managed payment method details')
        }
      } catch (err) {
        console.error(err)
        if (err.message === 'Checkout session is complete') {
          setIsLoading(false)
          trackHostedCheckoutStep('crypto', 'paymentConfirmed')
          setIsSuccess(true)
          setSuccessElement(<PaymentSuccessMessage />)
        } else {
          trackHostedCheckoutStep('crypto', 'paymentFailed')
          setErrorMessage(err.reason || err.message)
          setDiscountLocked(false)
        }
      }

      setIsPreProcessingPayment(false)
    }

    if (completeTokenDelegate) {
      const { paymentId, signature } = completeTokenDelegate

      setIsPreProcessingPayment(true)
      try {
        trackHostedCheckoutStep('crypto', 'paymentInitiated')
        await Radom.completeTokenDelegatePayment(paymentId, signature)
        if (checkoutId && tokenDelegatePaymentDetails) {
          savePaymentSession(checkoutId, {
            tokenDelegate: tokenDelegatePaymentDetails,
            delegateSubmitted: true
          })
        }
        startTokenDelegatePolling(paymentId)
      } catch (err) {
        trackHostedCheckoutStep('crypto', 'paymentFailed')
        setErrorMessage(err.reason || err.message)
        setIsPreProcessingPayment(false)
      }
    }
  }

  const redeemDiscountCode = async (discountCode: string): Promise<void> => {
    if (!checkoutId) {
      return
    }

    setDiscountCode(discountCode)
    setDiscountResult({ success: false, element: <Spinner /> })

    Radom.redeemDiscountCode({ checkoutSessionId: checkoutId, code: discountCode }).then((discount) => {
      setDiscount(discount)
      setDiscountResult({
        success: true,
        element: <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 14 }}>
          <span>Discount applied!</span>
          <span style={{
            padding: 5,
            backgroundColor: RADOM_COLORS.SUCCESS,
            color: RADOM_COLORS.SUCCESS_LIGHT,
            borderRadius: 5,
            fontWeight: 600,
            fontSize: 12
          }}>
            {
              discount.model.modelType === 'percentage'
                ? new Intl.NumberFormat('en-US', {
                  style: 'percent',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }).format(discount.model.amountOff / 100)
                : formatCurrency(currency, discount.model.amountOff)
            } OFF
          </span>
        </div>
      })
    }).catch(_ => {
      setDiscountResult({
        success: false,
        element: <span style={{ opacity: 0.5, fontSize: 12 }}>Invalid discount code</span>
      })
    })
  }

  const resetDiscountCode = (): void => {
    setDiscountResult({ success: false, element: <></> })
    setDiscount(undefined)
    setDiscountCode('')
  }

  const [renderOpenBankingState, setRenderOpenBankingState] =
    useState<OpenBankingRenderStates>(OpenBankingRenderStates.onramp)

  useEffect(() => {
    document.title = 'Radom - Checkout'
    getCheckoutData()
      .finally(() => setIsLoading(false))
  }, [])

  useEffect(() => {
    if (!isOpenBankingRedirectReturn || !checkoutId || !hasOpenBankingCheckout) {
      return
    }

    trackHostedCheckoutStep('openBanking', 'bankRedirectReturned', {
      sessionStatus: checkoutData?.sessionStatus,
      openBankingStatus: checkoutData?.openBankingPaymentData?.status
    })
  }, [
    checkoutId,
    hasOpenBankingCheckout,
    isOpenBankingRedirectReturn,
    trackHostedCheckoutStep
  ])

  useEffect(() => {
    if (!isOpenBankingRedirectReturn || !checkoutId || !hasOpenBankingCheckout) {
      return
    }

    if (
      openbankPayStatus === PaymentStatus.SUCCESS ||
      openbankPayStatus === PaymentStatus.Expired ||
      openbankPayStatus === PaymentStatus.ERROR
    ) {
      return
    }

    const clearReturnInterval = (): void => {
      if (openBankingReturnIntervalRef.current) {
        clearInterval(openBankingReturnIntervalRef.current)
        openBankingReturnIntervalRef.current = null
      }
    }

    const refreshOpenBankingReturnStatus = async (): Promise<void> => {
      try {
        const res = await Radom.getCheckoutSession(checkoutId, {
          refreshOpenBankingStatus: true
        })
        const openBankingGateway = res.gateway.openBanking

        setCheckoutData(prev => {
          if (!prev) {
            return prev
          }

          return {
            ...prev,
            sessionStatus: res.sessionStatus,
            gateway: {
              ...prev.gateway,
              openBanking: openBankingGateway ?? prev.gateway.openBanking
            },
            openBankingPaymentData: res.openBankingPaymentData
          }
        })

        setRenderOpenBankingState(OpenBankingRenderStates.summary)

        if (
          res.sessionStatus === 'success' ||
          res.sessionStatus === 'refunded'
        ) {
          localStorage.removeItem('OpenBankingAprove')
          setOpenbankPayStatus(PaymentStatus.SUCCESS)
          clearReturnInterval()

          if (!hasOpenBankingDestinationAddress(openBankingGateway?.destinationCryptoAddress) && res.successUrl) {
            setTimeout(() => {
              window.location.href = resolveNavigationUrl(res.successUrl)
            }, 1500)
          }
          return
        }

        if (res.sessionStatus === 'expired' && !isOpenBankingPaymentInFlight(res.openBankingPaymentData)) {
          setOpenbankPayStatus(PaymentStatus.Expired)
          clearReturnInterval()
          return
        }

        if (res.sessionStatus === 'cancelled') {
          if (res.cancelUrl) {
            window.location.href = resolveNavigationUrl(res.cancelUrl)
          } else {
            setErrorMessage(
              getOpenBankingReturnStatusCopy(res.openBankingPaymentData, res.sessionStatus).body
            )
            setOpenbankPayStatus(PaymentStatus.ERROR)
          }
          clearReturnInterval()
          return
        }

        if (isOpenBankingProviderFailed(res.openBankingPaymentData)) {
          setErrorMessage(
            getOpenBankingReturnStatusCopy(res.openBankingPaymentData, res.sessionStatus).body
          )
          setOpenbankPayStatus(PaymentStatus.ERROR)
          clearReturnInterval()
          return
        }

        if (isOpenBankingProviderConfirmed(res.openBankingPaymentData)) {
          localStorage.removeItem('OpenBankingAprove')
        }

        setOpenbankPayStatus(PaymentStatus.KYC_PENDING_VIEW)
      } catch (err) {
        console.error('Failed to refresh open banking checkout after bank redirect', err)
        setOpenbankPayStatus(PaymentStatus.KYC_PENDING_VIEW)
      }
    }

    clearReturnInterval()
    openBankingReturnIntervalRef.current = setInterval(() => {
      void refreshOpenBankingReturnStatus()
    }, 3000)
    void refreshOpenBankingReturnStatus()

    return clearReturnInterval
  }, [
    checkoutId,
    hasOpenBankingCheckout,
    isOpenBankingRedirectReturn,
    openbankPayStatus
  ])

  useEffect(() => {
    const discountCode = searchParams.get('discountCode')
    if (discountCode) {
      setDiscountCode(discountCode)
      redeemDiscountCode(discountCode)
    }
  }, [searchParams])

  if (loadError) {
    return <PageLoadState
      title="Checkout unavailable"
      message={loadError}
      onRetry={async () => {
        setIsLoading(true)
        await getCheckoutData().finally(() => setIsLoading(false))
      }}
    />
  }

  if (isExpired) {
    return (
      <PageLoadState
        title="Checkout session expired"
        message="This checkout session has expired. Start again to generate a fresh payment request."
        retryLabel={checkoutData?.cancelUrl ? 'Return to merchant' : 'Refresh checkout'}
        onRetry={async () => {
          if (checkoutData?.cancelUrl) {
            window.location.href = resolveNavigationUrl(checkoutData.cancelUrl)
            return
          }

          setIsLoading(true)
          await getCheckoutData().finally(() => setIsLoading(false))
        }}
      />
    )
  }

  if (posMode) {
    return <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'white',
      zIndex: 100,
      display: 'flex',
      flexDirection: 'column',
      padding: '30px',
      alignItems: 'center',
      justifyContent: 'space-between',
      overflow: 'hidden'
    }}>

      <span style={{ fontWeight: 200 }}>
        {formatCurrency(currency, totalSum)}
      </span>

      {
        !isComplete && checkoutData &&
        <div style={{
          maxWidth: '600px',
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          margin: 'auto'
        }}>
          <PayWidget
            currency={currency}
            checkoutData={checkoutData}
            isPreProcessingPayment={isPreProcessingPayment}
            isSuccess={isSuccess}
            onCheckout={onCheckout}
            managedPaymentDetails={managedPaymentDetails}
            onChangePaymentMethod={() => {
              clearManagedPaymentPolling()
              if (checkoutId) {
                clearPaymentSession(checkoutId)
              }
              setManagedPaymentDetails(undefined)
              setTokenDelegatePaymentDetails(undefined)
              setPaymentStatus(undefined)
              setErrorMessage('')
              setIsPreProcessingPayment(false)
            }}
            onQuoteUpdate={() => getPriceQuotes()}
            errorMessage={errorMessage}
            successElement={successElement}
            paymentStatus={paymentStatus}
            posMode
            travelRuleFieldsCollected={travelRuleFieldsCollected}
          />
        </div>
      }

    </div>
  }

  if (isOpenbank) {
    const isIvyCheckoutVisible = openbankPayStatus === PaymentStatus.WAITING

    return <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: isIvyCheckoutVisible ? '100dvh' : 'auto',
      minHeight: '100dvh',
      fontSize: 15,
      background: '#d9dddf',
      overflow: isIvyCheckoutVisible ? 'hidden' : 'visible'
    }}>
      <div style={{
        position: 'relative',
        padding: isIvyCheckoutVisible ? 0 : '40px 30px',
        background: 'white',
        minHeight: isIvyCheckoutVisible ? 'auto' : 680,
        height: isIvyCheckoutVisible ? 'min(760px, calc(100dvh - 20px))' : 'auto',
        // minHeight: 650,
        transition: '0.2s ease height',
        width: isIvyCheckoutVisible ? 'min(100vw, 520px)' : '95vw',
        maxWidth: isIvyCheckoutVisible ? '520px' : '500px',
        borderRadius: isIvyCheckoutVisible ? 16 : 15,
        zIndex: 2,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: isIvyCheckoutVisible ? 0 : 30,
        marginTop: isIvyCheckoutVisible ? 0 : 10,
        marginBottom: isIvyCheckoutVisible ? 0 : 10,
        overflow: isIvyCheckoutVisible ? 'hidden' : 'visible',
        boxShadow: isIvyCheckoutVisible ? '0 18px 50px rgba(15, 23, 42, 0.18)' : undefined
      }}>
        {
          openbankPayStatus === PaymentStatus.SUCCESS &&
          <div style={{
            display: 'flex',
            position: 'relative',
            margin: '10px 20px',
            background: 'white',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 600
          }}>
            {
              checkoutData?.gateway.openBanking?.destinationCryptoAddress === null && <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexDirection: 'column'
              }}>
                <CheckmarkWrapper style={{ background: RADOM_COLORS.NEW_BLUE, width: 45, height: 45 }}>
                  <Checkmark animationDelay='0.5s' animationSpeed='0.75s' stroke={'white'} />
                </CheckmarkWrapper>
                <span style={{ fontSize: 18, color: RADOM_COLORS.BLACK }}>Payment successful</span>
              </div>
            }

            {
              checkoutData?.gateway.openBanking?.destinationCryptoAddress && (
                <div style={{
                  fontSize: 16
                }}>

                  {/* Title */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    transform: 'translateY(-80px)',
                    alignItems: 'center',
                    marginBottom: 5,
                    width: '100%',
                    fontWeight: 600,
                    fontSize: 26,
                    color: RADOM_COLORS.GRAY4
                  }}>
                    <USDCLogo
                      style={{ width: 48, height: 48, marginBottom: 12 }}
                    />
                    <div>
                        Buying
                      <span style={{
                        color: 'black',
                        fontWeight: 600,
                        marginLeft: 5
                      }}> {formatOnrampAmount(checkoutData.gateway.openBanking.onrampAmount) ?? ''}  USDC</span>
                    </div>
                    <div>
                        for {formatCurrency(checkoutData.currency ?? '', totalSum)}
                    </div>
                  </div>

                  {/* Step Progress */}
                  <ul style={{
                    position: 'relative',
                    paddingLeft: 45,
                    listStyle: 'none',
                    margin: 0,
                    transform: 'translateY(-20px)'
                  }}>
                    {/* 竖线 */}
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 15,
                        width: 0,
                        height: '95%',
                        borderLeft: '2px solid #CCC',
                        zIndex: 0
                      }}
                    />

                    {/* Step 1 - done */}
                    <li style={{ position: 'relative', paddingBottom: 20, zIndex: 1 }}>
                      <span
                        style={{
                          position: 'absolute',
                          left: -37,
                          top: 0,
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          border: '2px solid green',
                          background: 'green',
                          color: '#fff',
                          fontSize: 10,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
            ✔
                      </span>
                      <strong>Transaction created</strong>
                      <div style={{ color: '#6B6C7E', fontSize: 14 }}>{
                        checkoutData.createdAt ? new Date(checkoutData.createdAt).toLocaleString() : ''}</div>
                    </li>

                    {/* Step 2 - done */}
                    <li style={{ position: 'relative', paddingBottom: 20, zIndex: 1 }}>
                      <span
                        style={{
                          position: 'absolute',
                          left: -37,
                          top: 0,
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          border: '2px solid green',
                          background: 'green',
                          color: '#fff',
                          fontSize: 10,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
            ✔
                      </span>
                      <strong

                      >Verification successful</strong>
                      <div style={{ color: '#6B6C7E', fontSize: 14 }}>
                        {checkoutData.updatedAt ? new Date(checkoutData.updatedAt).toLocaleString() : ''}</div>
                    </li>

                    {/* Step 3 - done */}
                    <li style={{ position: 'relative', paddingBottom: 20, zIndex: 1 }}>
                      <span
                        style={{
                          position: 'absolute',
                          left: -37,
                          top: 0,
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          border: '2px solid green',
                          background: 'green',
                          color: '#fff',
                          fontSize: 10,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
            ✔
                      </span>
                      <strong

                      >Payment confirmed</strong>
                      <div style={{ color: '#6B6C7E', fontSize: 14 }}>{
                        checkoutData.updatedAt ? new Date(checkoutData.updatedAt).toLocaleString() : ''
                      }</div>
                    </li>

                    {/* Step 4 - current */}
                    <li style={{ position: 'relative', paddingBottom: 20, zIndex: 1 }}>
                      <span
                        style={{
                          position: 'absolute',
                          left: -37,
                          top: 0,
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          border: checkoutData?.gateway.openBanking?.instantPaymentStatus
                            ? '2px solid green'
                            : '2px solid green',
                          background: checkoutData?.gateway.openBanking?.instantPaymentStatus ? 'green' : '#fff',
                          color: checkoutData?.gateway.openBanking?.instantPaymentStatus ? '#fff' : 'green',
                          fontSize: 10,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {checkoutData?.gateway.openBanking?.instantPaymentStatus
                          ? '✔'
                          : <div style={{
                            width: 10,
                            height: 10,
                            border: '2px solid green',
                            borderTop: '2px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }} />}
                      </span>
                      <strong>Final checks in progress</strong>
                      <div style={{ color: '#6B6C7E', fontSize: 14 }}>
                        {new Date().toLocaleString()}
                      </div>
                      <div style={{ fontSize: 14, color: '#6B6C7E' }}>
                        {"We're completing final checks to keep your transaction secure and compliant with local regulations."}                      </div>
                    </li>

                    <style>
                      {`
                    @keyframes spin {
                      from { transform: rotate(0deg); }
                      to { transform: rotate(360deg); }
                    }
                    `}
                    </style>

                    {/* Step 5 - todo */}
                    {checkoutData?.gateway.openBanking?.instantPaymentStatus
                      ? (
                        <li style={{ position: 'relative', zIndex: 1 }}>
                          <span
                            style={{
                              position: 'absolute',
                              left: -37,
                              top: 0,
                              width: 14,
                              height: 14,
                              borderRadius: '50%',
                              border: '2px solid green',
                              background: 'green',
                              color: '#fff',
                              fontSize: 10,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                          ✔
                          </span>
                          <strong>Crypto delivered</strong>
                        </li>
                      )
                      : (
                        <li style={{ position: 'relative', zIndex: 1 }}>
                          <span
                            style={{
                              position: 'absolute',
                              left: -37,
                              top: 0,
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              border: '2px solid #CCC',
                              background: '#fff'
                            }}
                          />
                          <strong style={{ color: '#6B6C7E' }}>Crypto delivery</strong>
                        </li>
                      )}
                  </ul>
                </div>
              )
            }

            <div style={{
              bottom: 0,
              position: 'absolute',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 10,
              marginBottom: 30
            }}>
              <a
                href='https://www.radom.com/crypto-presales'
                target='_blank'
                title='Crypto Presale Powered by Radom'
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  cursor: 'pointer',
                  userSelect: 'none',
                  textDecoration: 'none',
                  color: RADOM_COLORS.GRAY_DARKEST
                }} rel="noreferrer"
              >
                <span style={{ display: 'flex', alignItems: 'center', fontSize: 13, fontWeight: 500 }}>
                  Powered by
                  <RadomLogo
                    width={60}
                    height={14}
                    fill={RADOM_COLORS.GRAY_DARKEST}
                  />
                </span>
                <span style={{ position: 'absolute', width: '1px', height: '1px', margin: '-1px', padding: 0, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', border: 0 }}>
                  Radom
                </span>
              </a>
            </div>
          </div>
        }

        {
          openbankPayStatus === PaymentStatus.WAITING &&
          <IvyCheckout
            data-hj-allow-iframe
            checkoutUrl={openBankingCheckoutUrl}
            displayOptions={{
              type: 'embedded',
              maxHeight: 760,
              maxWidth: 520
            }}
            onSuccess={() => {
              setOpenbankPayStatus(PaymentStatus.KYC_PENDING_VIEW)
              setRenderOpenBankingState(OpenBankingRenderStates.summary)

              if (openBankingIntervalRef.current) {
                clearInterval(openBankingIntervalRef.current)
              }

              const refreshAfterIvySuccess = (): void => {
                void Radom.getCheckoutSession(checkoutId ?? '', {
                  refreshOpenBankingStatus: true
                }).then(res => {
                  const openBankingGateway = res.gateway.openBanking

                  setCheckoutData(prev => {
                    if (!prev) {
                      return prev
                    }

                    return {
                      ...prev,
                      sessionStatus: res.sessionStatus,
                      gateway: {
                        ...prev.gateway,
                        openBanking: openBankingGateway ?? prev.gateway.openBanking
                      },
                      openBankingPaymentData: res.openBankingPaymentData
                    }
                  })

                  if (res.sessionStatus === 'success' || res.sessionStatus === 'refunded') {
                    if (openBankingIntervalRef.current) {
                      clearInterval(openBankingIntervalRef.current)
                      openBankingIntervalRef.current = null
                    }

                    setOpenbankPayStatus(PaymentStatus.SUCCESS)

                    if (
                      !hasOpenBankingDestinationAddress(openBankingGateway?.destinationCryptoAddress) &&
                      res.successUrl
                    ) {
                      setTimeout(() => {
                        window.location.replace(resolveNavigationUrl(res.successUrl))
                      }, 1500)
                    }
                    return
                  }

                  if (res.sessionStatus === 'expired' && !isOpenBankingPaymentInFlight(res.openBankingPaymentData)) {
                    if (openBankingIntervalRef.current) {
                      clearInterval(openBankingIntervalRef.current)
                      openBankingIntervalRef.current = null
                    }
                    setOpenbankPayStatus(PaymentStatus.Expired)
                    return
                  }

                  if (res.sessionStatus === 'cancelled' || isOpenBankingProviderFailed(res.openBankingPaymentData)) {
                    if (openBankingIntervalRef.current) {
                      clearInterval(openBankingIntervalRef.current)
                      openBankingIntervalRef.current = null
                    }
                    setErrorMessage(getOpenBankingReturnStatusCopy(res.openBankingPaymentData, res.sessionStatus).body)
                    setOpenbankPayStatus(PaymentStatus.ERROR)
                  }
                }).catch(err => {
                  console.error('Failed to refresh open banking checkout after Ivy success', err)
                })
              }

              refreshAfterIvySuccess()
              openBankingIntervalRef.current = setInterval(refreshAfterIvySuccess, 3000)
            }}
            onCancel={() => {
              window.location.replace(resolveNavigationUrl(checkoutData?.cancelUrl))
            }}
          />
        }

        {
          openbankPayStatus === PaymentStatus.INITIAL &&
          <RenderOpenBankingInitial
            renderState={renderOpenBankingState}
            setRenderState={setRenderOpenBankingState}
            checkoutData={checkoutData}
            totalSum={totalSum}
            currency={currency}
            isPreProcessingPayment={isPreProcessingPayment}
            onrampAmount={checkoutData?.gateway.openBanking?.onrampAmount}
            randomAddress={randomAddress}
            userKycData={userKycData}
            setUserKycData={setUserKycData}
            addressSuggestionList={addressSuggestionList}
            setAddressSuggestionList={setAddressSuggestionList}
            allCountries={allCountries}
            debouncedFetch={debouncedFetch}
            checkMarkOpenbanking={checkMarkOpenbanking}
            setCheckMarkOpenBanking={setCheckMarkOpenBanking}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            setOpenbankPayStatus={setOpenbankPayStatus}
            setOpenBankingCheckoutUrl={setOpenBankingCheckoutUrl}
            openBankingCheckoutUrl={openBankingCheckoutUrl}
            setOpenBankingTotalUsdRate={() => {}}
          />
        }

        {
          openbankPayStatus === PaymentStatus.KYC &&
          <div>
            {
              isLoading &&
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100vw',
                  height: '100vh',
                  backgroundColor: 'rgba(0, 0, 0, 0.15)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 9999
                }}
              >
                <CheckmarkWrapper style={{ background: RADOM_COLORS.NEW_BLUE, width: 50, height: 50 }}>
                  <PendingSpinner animationDelay='0.5s' animationSpeed='0.75s' stroke={'white'} />
                </CheckmarkWrapper>
              </div>
            }
            <KycView
              kycTips={'KYC verification will apply if you deposit more than 1K EUR per single transaction or in total for 24 hours'}
              errorMessage={errorMessage}
              onBack={() => {
                setOpenbankPayStatus(PaymentStatus.INITIAL)
                if (openBankingIntervalRef.current) {
                  clearInterval(openBankingIntervalRef.current)
                }
              }}
              setOpenbankPayStatus={setOpenbankPayStatus}
              onSubmit={() => {
                setIsLoading(true)
                setOpenbankPayStatus(PaymentStatus.KYC_PENDING_VIEW)
                openBankingIntervalRef.current = setInterval(() => {
                  Radom.getCheckoutSession(checkoutId ?? '').then((res) => {
                    const checkoutUrl = res.gateway.openBanking?.openBankingCheckoutUrl
                    if (checkoutUrl && checkoutUrl !== '') {
                      if (isSecureHostedUrl(checkoutUrl)) {
                        setOpenBankingCheckoutUrl(checkoutUrl)
                        setOpenbankPayStatus(PaymentStatus.WAITING)
                        if (openBankingIntervalRef.current) {
                          clearInterval(openBankingIntervalRef.current)
                        }
                      } else {
                        setErrorMessage(formatOpenBankingErrorMessage(checkoutUrl))
                        setOpenbankPayStatus(PaymentStatus.ERROR)
                      }
                    }
                  })
                }, 2000)
              }}
              onChange={async (value) => {
                setIsLoading(true)
                if (value.kycType === 2) {
                  if (value.documentType === 'passport') {
                    setUserKycData({
                      ...userKycData,
                      passportFile: value.imageFile
                    })
                    await Radom.updateCustomerKycData(
                      checkoutId ?? '',
                      buildOpenBankingCustomerPayload(userKycData, {
                        passportFile: value.imageFile
                      })
                    ).finally(() => {
                      setIsLoading(false)
                    })
                  } else if (value.documentType === 'id_card') {
                    setUserKycData({
                      ...userKycData,
                      idCardFile: value.imageFile
                    })
                    await Radom.updateCustomerKycData(
                      checkoutId ?? '',
                      buildOpenBankingCustomerPayload(userKycData, {
                        idCardFile: value.imageFile
                      })
                    ).finally(() => {
                      setIsLoading(false)
                    })
                  }
                } else if (value.kycType === 3) {
                  await Radom.updateCustomerKycData(
                    checkoutId ?? '',
                    buildOpenBankingCustomerPayload(userKycData, {
                      selfieFile: value.imageFile
                    })
                  ).finally(() => {
                    setIsLoading(false)
                  })
                  setUserKycData({
                    ...userKycData,
                    selfieFile: value.imageFile
                  })
                }
              }}
            />
          </div>
        }

        {
          openbankPayStatus === PaymentStatus.Expired &&
          <div style={{
            display: 'flex',
            position: 'relative',
            margin: '10px 20px',
            background: '#f5f6f6',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 600
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexDirection: 'column'
            }}>
              <CheckmarkWrapper style={{ background: RADOM_COLORS.ERROR, width: 45, height: 45 }}>
                <Crossmark animationDelay='0.5s' animationSpeed='0.75s' stroke={'white'} />
              </CheckmarkWrapper>
              <span style={{ fontSize: 18, color: RADOM_COLORS.BLACK }}>Payment Expired</span>
            </div>

            <div style={{
              bottom: 0,
              position: 'absolute',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 10,
              marginBottom: 30
            }}>
              <a
                href='https://www.radom.com/crypto-presales'
                target='_blank'
                title='Crypto Presale Powered by Radom'
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  cursor: 'pointer',
                  userSelect: 'none',
                  textDecoration: 'none',
                  color: RADOM_COLORS.GRAY_DARKEST
                }} rel="noreferrer"
              >
                <span style={{ display: 'flex', alignItems: 'center', fontSize: 13, fontWeight: 500 }}>
                  Powered by
                  <RadomLogo
                    width={60}
                    height={14}
                    fill={RADOM_COLORS.GRAY_DARKEST}
                  />
                </span>
                <span style={{ position: 'absolute', width: '1px', height: '1px', margin: '-1px', padding: 0, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', border: 0 }}>
                  Radom
                </span>
              </a>
            </div>
          </div>
        }

        {
          openbankPayStatus === PaymentStatus.ERROR &&
          <div style={{
            display: 'flex',
            position: 'relative',
            margin: '10px 20px',
            background: '#f5f6f6',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 600
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexDirection: 'column'
            }}>
              <CheckmarkWrapper style={{ background: RADOM_COLORS.ERROR, width: 45, height: 45 }}>
                <Crossmark animationDelay='0.2s' animationSpeed='0.15s' stroke={'white'} />
              </CheckmarkWrapper>
              <span style={{ fontSize: 18, color: RADOM_COLORS.BLACK }}>
                {openBankingReturnStatusCopy.isError ? openBankingReturnStatusCopy.title : 'Verification required'}
              </span>

              {
                (errorMessage || openBankingReturnStatusCopy.isError) &&
              <div style={{
                backgroundColor: RADOM_COLORS.ERROR,
                padding: 15,
                borderRadius: 5,
                marginTop: 20,
                // marginBottom: 20,
                color: 'white',
                overflowWrap: 'break-word'
              }}>
                {errorMessage || openBankingReturnStatusCopy.body}
              </div>
              }
            </div>

            <div style={{
              bottom: 0,
              position: 'absolute',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 10,
              marginBottom: 30
            }}>
              <a
                href='https://www.radom.com/crypto-presales'
                target='_blank'
                title='Crypto Presale Powered by Radom'
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  cursor: 'pointer',
                  userSelect: 'none',
                  textDecoration: 'none',
                  color: RADOM_COLORS.GRAY_DARKEST
                }} rel="noreferrer"
              >
                <span style={{ display: 'flex', alignItems: 'center', fontSize: 13, fontWeight: 500 }}>
                  Powered by
                  <RadomLogo
                    width={60}
                    height={14}
                    fill={RADOM_COLORS.GRAY_DARKEST}
                  />
                </span>
                <span style={{ position: 'absolute', width: '1px', height: '1px', margin: '-1px', padding: 0, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', border: 0 }}>
                  Radom
                </span>
              </a>
            </div>
          </div>
        }

        {
          openbankPayStatus === PaymentStatus.KYC_PENDING_VIEW &&
          <div style={{
            display: 'flex',
            position: 'relative',
            margin: '10px 20px',
            background: 'white',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 600
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexDirection: 'column'
            }}>
              <CheckmarkWrapper style={{ background: RADOM_COLORS.NEW_BLUE, width: 60, height: 60 }}>
                <PendingSpinner animationDelay='0.5s' animationSpeed='0.75s' stroke={'white'} />
              </CheckmarkWrapper>
              <span style={{ fontSize: 18, color: RADOM_COLORS.BLACK }}>{openBankingReturnStatusCopy.title}</span>
              <span style={{
                color: RADOM_COLORS.GRAY_DARKEST,
                fontSize: 14,
                lineHeight: 1.4,
                maxWidth: 300,
                textAlign: 'center'
              }}>
                {openBankingReturnStatusCopy.body}
              </span>

            </div>

            <div style={{
              bottom: 0,
              position: 'absolute',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 10,
              marginBottom: 30
            }}>
              <a
                href='https://www.radom.com/crypto-presales'
                target='_blank'
                title='Crypto Presale Powered by Radom'
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  cursor: 'pointer',
                  userSelect: 'none',
                  textDecoration: 'none',
                  color: RADOM_COLORS.GRAY_DARKEST
                }} rel="noreferrer"
              >
                <span style={{ display: 'flex', alignItems: 'center', fontSize: 13, fontWeight: 500 }}>
                  Powered by
                  <RadomLogo
                    width={60}
                    height={14}
                    fill={RADOM_COLORS.GRAY_DARKEST}
                  />
                </span>
                <span style={{ position: 'absolute', width: '1px', height: '1px', margin: '-1px', padding: 0, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', border: 0 }}>
                  Radom
                </span>
              </a>
            </div>
          </div>
        }

      </div>

    </div >
  }

  if (!checkoutData) {
    return <Container>{!isLoading && <div>Checkout id not found</div>}</Container>
  }

  return (
    <Checkout
      checkoutData={checkoutData}
      isPreProcessingPayment={isPreProcessingPayment}
      isSuccess={isSuccess}
      onCheckout={onCheckout as any}
      managedPaymentDetails={managedPaymentDetails}
      tokenDelegatePaymentDetails={tokenDelegatePaymentDetails}
      onChangePaymentMethod={() => {
        clearManagedPaymentPolling()
        if (checkoutId) {
          clearPaymentSession(checkoutId)
        }
        setManagedPaymentDetails(undefined)
        setTokenDelegatePaymentDetails(undefined)
        setPaymentStatus(undefined)
        setErrorMessage('')
        setIsPreProcessingPayment(false)
      }}
      successElement={successElement}
      errorMessage={errorMessage}
      onDiscountCodeEntered={redeemDiscountCode}
      resetDiscountCode={resetDiscountCode}
      discountResult={discountResult}
      discount={discount}
      discountLocked={discountLocked}
      paymentStatus={paymentStatus}
      travelRuleFieldsCollected={travelRuleFieldsCollected}
      onCurrency={(value) => setCurrency(value)}
      onTotalChange={(value) => setTotalSum(value)}
    />
  )
}

export default HostedCheckout
